(() => {
  if (window.AvonticAnalytics?.version) return;

  const config = window.AvonticAnalyticsConfig || {};
  const searchParams = new URLSearchParams(window.location.search);
  const previewRoles = new Set(['development', 'demo', 'unpublished']);
  const isPreviewContext = Boolean(window.Shopify?.designMode)
    || searchParams.has('preview_theme_id')
    || previewRoles.has(String(config.themeRole || '').toLowerCase());
  const debugEnabled = isPreviewContext && searchParams.get('av_debug_analytics') === '1';
  const recent = new Map();
  const debugEvents = [];
  const allowedFields = new Set([
    'component', 'component_instance', 'surface', 'position', 'product_id', 'product_handle',
    'variant_id', 'collection_id', 'collection_handle', 'compatibility_key', 'compatibility_state',
    'recommendation_rule_id', 'recommendation_fit_type', 'source_product_id',
    'recommended_product_id', 'recommended_variant_id', 'currency', 'price_cents', 'quantity',
    'result', 'reason', 'experiment_id', 'experiment_variant', 'action', 'result_count',
    'visibility_ratio', 'requested_variant_id', 'cart_item_count', 'search_result_count',
    'destination', 'search_context', 'total_products', 'complete_products', 'added_products',
    'variant_ids', 'cart_state', 'engine_version', 'setup_progress', 'source_family',
    'relationship_type'
  ]);

  const cleanName = (value) => {
    const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    if (!normalized) return '';
    return normalized.startsWith('av_') ? normalized : `av_${normalized}`;
  };

  const cleanDestination = (value) => {
    try {
      const url = new URL(String(value), window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return url.protocol.replace(':', '');
      return `${url.origin === window.location.origin ? '' : url.origin}${url.pathname}`.slice(0, 160);
    } catch (_) {
      return String(value || '').split(/[?#]/, 1)[0].slice(0, 160);
    }
  };

  const cleanValue = (value, key) => {
    if (key === 'destination') return cleanDestination(value);
    if (key === 'variant_ids' && Array.isArray(value)) {
      return value.slice(0, 20).map((item) => String(item).slice(0, 40));
    }
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'string') return value.slice(0, 160);
    return undefined;
  };

  const sanitizedPayload = (payload) => {
    const safe = {};
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (!allowedFields.has(key)) return;
      const cleaned = cleanValue(value, key);
      if (cleaned !== undefined && cleaned !== '') safe[key] = cleaned;
    });
    return safe;
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function'
        && privacy.analyticsProcessingAllowed());
    } catch (_) {
      return false;
    }
  };

  const eventId = () => {
    try {
      return crypto.randomUUID();
    } catch (_) {
      return `av-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return (result >>> 0).toString(36);
  };

  const debug = (status, detail) => {
    if (!debugEnabled) return;
    const entry = { status, consent_allowed: analyticsAllowed(), ...detail };
    debugEvents.push(entry);
    if (debugEvents.length > 100) debugEvents.shift();
    console.info('[Avontic analytics]', entry);
  };

  const track = (requestedName, payload = {}, options = {}) => {
    try {
      const eventName = cleanName(requestedName);
      if (!eventName) return false;

      const safePayload = sanitizedPayload(payload);
      if (!analyticsAllowed()) {
        debug('blocked_without_consent', { event_name: eventName });
        return false;
      }

      const signature = hash(`${eventName}:${options.dedupeKey || JSON.stringify(safePayload)}`);
      const now = Date.now();
      const lastSeen = recent.get(signature);
      const dedupeWindow = Number(options.dedupeWindow || 1200);

      if (lastSeen && now - lastSeen < dedupeWindow) {
        debug('duplicate_suppressed', { event_name: eventName, signature });
        return false;
      }

      if (options.oncePerSession) {
        const storageKey = `avontic:event:${signature}`;
        try {
          if (sessionStorage.getItem(storageKey)) {
            debug('session_duplicate_suppressed', { event_name: eventName, signature });
            return false;
          }
          sessionStorage.setItem(storageKey, '1');
        } catch (_) {
          // In-memory deduplication remains available when storage is blocked.
        }
      }

      recent.set(signature, now);
      if (recent.size > 250) {
        for (const [key, timestamp] of recent) {
          if (now - timestamp > 60000) recent.delete(key);
        }
      }

      const detail = {
        event_id: options.eventId || eventId(),
        event_name: eventName,
        event_version: '1.0',
        occurred_at: new Date().toISOString(),
        page_type: String(config.pageType || '').slice(0, 60),
        page_path: window.location.pathname.slice(0, 200),
        template_suffix: String(config.templateSuffix || '').slice(0, 80),
        ...safePayload
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, avontic: detail });

      let shopifyPublished = false;
      try {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
          window.Shopify.analytics.publish(eventName, detail);
          shopifyPublished = true;
        }
      } catch (_) {
        // Analytics must never interrupt navigation or commerce.
      }

      document.dispatchEvent(new CustomEvent('avontic:analytics', { detail }));
      debug('sent', {
        event_name: eventName,
        event_id: detail.event_id,
        destinations: { data_layer: true, shopify_analytics: shopifyPublished }
      });
      return true;
    } catch (_) {
      return false;
    }
  };

  const closest = (target, selector) => target instanceof Element ? target.closest(selector) : null;
  const componentInstance = (element) => {
    const section = element?.closest?.('[id^="shopify-section-"]');
    return section?.id?.replace('shopify-section-', '').slice(0, 120) || undefined;
  };

  const trackPageView = () => {
    const common = {
      product_id: config.productId,
      product_handle: config.productHandle,
      variant_id: config.variantId,
      collection_id: config.collectionId,
      collection_handle: config.collectionHandle,
      currency: config.currency
    };
    if (config.pageType === 'product') track('av_product_view', common, { oncePerSession: true });
    if (config.pageType === 'collection') track('av_collection_view', common, { oncePerSession: true });
    if (config.pageType === 'cart') track('av_cart_view', { cart_item_count: config.cartItemCount }, { oncePerSession: true });
  };

  const impressions = [
    ['.av-ph', 'av_performance_hall_view'],
    ['.product-card', 'av_product_card_impression']
  ];

  let impressionObserver;

  const observeImpressions = (root = document) => {
    if (!('IntersectionObserver' in window)) return;
    if (!impressionObserver) {
      impressionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
          const eventName = entry.target.dataset.avImpressionEvent;
          const instance = componentInstance(entry.target);
          const productId = entry.target.dataset.productId || entry.target.dataset.recommendedProductId;
          track(eventName, {
            component_instance: instance,
            product_id: productId,
            recommended_product_id: entry.target.dataset.recommendedProductId,
            surface: entry.target.dataset.surface,
            position: Number(entry.target.dataset.position) || undefined,
            visibility_ratio: Math.round(entry.intersectionRatio * 100) / 100
          }, { oncePerSession: true, dedupeKey: `${instance}:${productId || entry.target.dataset.position || ''}` });
          impressionObserver.unobserve(entry.target);
        });
      }, { threshold: [0.35] });
    }

    impressions.forEach(([selector, eventName]) => {
      root.querySelectorAll?.(selector).forEach((element) => {
        if (element.dataset.avImpressionObserved) return;
        element.dataset.avImpressionObserved = 'true';
        element.dataset.avImpressionEvent = eventName;
        impressionObserver.observe(element);
      });
    });
  };

  const clickRules = [
    ['.avh__actions a:first-of-type', 'av_home_hero_primary_click'],
    ['.avh__actions a:nth-of-type(2)', 'av_home_hero_secondary_click'],
    ['.avdn__card', 'av_device_navigation_select'],
    ['[data-ph-tab]', 'av_performance_hall_tab_select'],
    ['.av-ph__cta', 'av_performance_hall_product_click'],
    ['.product-card a[href*="/products/"]:not([data-av-choose-options])', 'av_product_card_click'],
    ['a[href*="/pages/contact"]:not([data-av-discovery-action="compatibility_help"]), a[href^="mailto:"]', 'av_support_click'],
    ['.shopify-payment-button__button', 'av_dynamic_checkout_click'],
    ['.product-sticky-bar button, [data-sticky-add-to-cart]', 'av_sticky_add_to_cart_click']
  ];

  document.addEventListener('click', (event) => {
    const target = event.target;
    const menuToggle = closest(target, 'button[aria-controls*="menu" i], button[aria-controls*="header-sidebar" i]');
    if (menuToggle) {
      track(menuToggle.getAttribute('aria-expanded') === 'true' ? 'av_navigation_close' : 'av_navigation_open', {
        component: 'global_navigation',
        component_instance: componentInstance(menuToggle)
      });
    }

    const navItem = closest(target, 'header a[href], header-sidebar a[href]');
    if (navItem) track('av_navigation_item_click', { component: 'global_navigation', component_instance: componentInstance(navItem) });

    const searchOpen = closest(target, 'button[aria-controls*="search" i], a[href="/search"]');
    if (searchOpen) track('av_search_open', { component: 'global_search' });

    const discoveryDevice = closest(target, '[data-av-discovery-action="device_navigation"]');
    if (discoveryDevice) {
      const discoveryContext = discoveryDevice.closest('[data-av-discovery-context]')?.dataset.avDiscoveryContext || 'collection';
      track(discoveryContext === 'search' ? 'av_search_recovery_click' : 'av_collection_device_select', {
        component: 'device_navigation',
        destination: discoveryDevice.dataset.avDiscoveryDestination,
        collection_handle: config.collectionHandle,
        search_context: discoveryContext === 'search' ? 'device_navigation' : undefined
      });
    }

    const searchRecovery = closest(target, '[data-av-discovery-context^="search"] [data-av-discovery-action="compatibility_help"]');
    if (searchRecovery) {
      track('av_search_recovery_click', {
        component: 'search_recovery',
        destination: searchRecovery.getAttribute('href')
      });
    }

    const cardChoose = closest(target, '.product-card [data-av-choose-options]');
    if (cardChoose) {
      const card = cardChoose.closest('.product-card');
      track('av_choose_options_click', {
        component: 'product_card',
        component_instance: componentInstance(cardChoose),
        product_id: card?.dataset.productId,
        product_handle: card?.dataset.avProductHandle
      });
    }

    const cardQuickAdd = closest(target, '.product-card__quick-add-button');
    if (cardQuickAdd) {
      const isDirectAdd = cardQuickAdd.matches('button[type="submit"]');
      track(isDirectAdd ? 'av_add_to_cart_attempt' : 'av_choose_options_click', {
        component: 'product_card',
        component_instance: componentInstance(cardQuickAdd),
        result: isDirectAdd ? 'attempted' : undefined
      });
    }

    const checkout = closest(target, '[name="checkout"], a[href^="/checkout"]');
    if (checkout) track('av_checkout_start', { component: 'cart' });

    const remove = closest(target, '.line-item-quantity a.link, line-item a[href*="/cart/change"]');
    if (remove) track('av_cart_remove', { component: 'cart' });

    const addButton = closest(target, 'button[name="add"], [data-av-atc-control]');
    if (addButton) {
      const blocked = addButton.disabled || addButton.getAttribute('aria-disabled') === 'true' || addButton.dataset.compatibilityBlocked === 'true';
      track(blocked ? 'av_add_to_cart_blocked' : 'av_add_to_cart_attempt', {
        product_id: config.productId,
        product_handle: config.productHandle,
        variant_id: document.querySelector('form[action*="/cart/add"] [name="id"]')?.value,
        compatibility_state: addButton.closest('[data-av-compatibility-state]')?.dataset.avCompatibilityState,
        result: blocked ? 'blocked' : 'attempted',
        reason: blocked ? 'compatibility_or_availability' : undefined
      });
    }

    for (const [selector, eventName] of clickRules) {
      const element = closest(target, selector);
      if (!element) continue;
      track(eventName, {
        component_instance: componentInstance(element),
        product_id: element.dataset.productId,
        recommended_product_id: element.closest('[data-av-recommendation]')?.dataset.recommendedProductId,
        surface: element.closest('[data-av-recommendation]')?.dataset.surface,
        position: Number(element.dataset.position || element.closest('[data-position]')?.dataset.position) || undefined
      });
      break;
    }
  }, true);

  document.addEventListener('change', (event) => {
    const option = closest(event.target, 'variant-picker input, variant-picker select, form[action*="/cart/add"] [name="id"]');
    if (option) track('av_variant_option_select', { product_id: config.productId, variant_id: option.value });
    const filter = closest(event.target, '[name^="filter."], facet-dialog input, facet-dialog select');
    if (filter) track('av_collection_filter', { collection_id: config.collectionId, collection_handle: config.collectionHandle });
    const sort = closest(event.target, '[name="sort_by"]');
    if (sort) track('av_collection_sort', { collection_id: config.collectionId, collection_handle: config.collectionHandle });
    const quantity = closest(event.target, 'line-item quantity-selector input, line-item [name="updates[]"]');
    if (quantity) track('av_cart_quantity_change', { component: 'cart', quantity: Number(quantity.value) || 0 });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('form[action*="/search"]')) {
      track('av_search_submit', {
        component: 'global_search',
        search_context: form.closest('header-search') ? 'predictive' : 'full_site'
      });
    }
    if (form.matches('form[action*="/contact"]')) track('av_contact_form_start', { component: 'contact_form' });
  }, true);

  document.addEventListener('focusin', (event) => {
    const form = closest(event.target, 'form[action*="/contact"]');
    if (form) track('av_contact_form_start', { component: 'contact_form' }, { oncePerSession: true, dedupeKey: componentInstance(form) || 'contact' });
  });

  document.addEventListener('cart:change', (event) => {
    track('av_add_to_cart_success', {
      product_id: config.productId,
      product_handle: config.productHandle,
      variant_id: event.detail?.variant?.id || event.detail?.productVariantId,
      cart_item_count: event.detail?.cart?.item_count,
      result: 'success'
    });
  });

  document.addEventListener('variant:change', (event) => {
    track('av_variant_resolved', {
      product_id: config.productId,
      variant_id: event.detail?.variant?.id,
      result: event.detail?.variant?.available === false ? 'unavailable' : 'available'
    });
  });

  document.addEventListener('shopify:section:load', (event) => observeImpressions(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-av-impression-observed]').forEach((element) => {
      impressionObserver?.unobserve(element);
      delete element.dataset.avImpressionObserved;
      delete element.dataset.avImpressionEvent;
    });
  });
  document.addEventListener('visitorConsentCollected', () => {
    debug('consent_updated', {});
    trackPageView();
    observeImpressions();
  });

  window.AvonticAnalytics = Object.freeze({
    version: '1.0.0',
    track,
    consentAllowed: analyticsAllowed,
    isPreviewContext,
    debugEnabled,
    getDebugState: () => debugEnabled ? { consent_allowed: analyticsAllowed(), events: [...debugEvents] } : null
  });

  const ready = () => {
    trackPageView();
    observeImpressions();
    if (document.querySelector('.av-contact-form__status--success')) {
      track('av_contact_form_success', { component: 'contact_form', result: 'success' }, { oncePerSession: true });
    }
    if (document.querySelector('.av-contact-form__status--error')) {
      track('av_contact_form_error', { component: 'contact_form', result: 'error' }, { oncePerSession: true });
    }
    if (config.pageType === 'search' && document.querySelector('.predictive-search__no-results, [data-av-search-no-results]')) {
      track('av_search_no_results', {
        component: 'search',
        result: 'no_results',
        search_context: 'full_site',
        search_result_count: 0
      }, { oncePerSession: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
})();
